import React, { useEffect, useState, useCallback } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from "recharts";
import api from "../services/api";
import "./Analytics.css";

// Status + priority colors reused across every chart so the same
// color always means the same thing on this page.
const STATUS_COLORS = {
  Open: "#C0392B",
  "In Progress": "#B6791F",
  Resolved: "#1F7A4D"
};

const PRIORITY_COLORS = {
  HIGH: "#C0392B",
  MEDIUM: "#B6791F",
  LOW: "#8592A6"
};

const CATEGORY_BAR_COLOR = "#16325C";
const TREND_LINE_COLOR = "#C9A227";

// Try a few likely field names since the API shape for timestamps
// isn't guaranteed to use the same key everywhere.
const getCreatedAt = (c) => c?.createdAt || c?.date || c?.submittedAt;
const getResolvedAt = (c) => c?.resolvedAt || c?.closedAt || c?.updatedAt;

const formatHours = (hrs) => {
  if (hrs == null || Number.isNaN(hrs)) return "—";
  if (hrs < 1) return `${Math.round(hrs * 60)}m`;
  if (hrs < 48) return `${hrs.toFixed(1)}h`;
  return `${(hrs / 24).toFixed(1)}d`;
};

const Analytics = () => {

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {

      const res = await api.get("/api/corporator/complaints");

      const complaints = res.data || [];

      const total = complaints.length;

      const open = complaints.filter(
        c => c.status?.toUpperCase() === "OPEN"
      ).length;

      const progress = complaints.filter(
        c => c.status?.toUpperCase() === "IN_PROGRESS"
      ).length;

      const resolved = complaints.filter(
        c => c.status?.toUpperCase() === "RESOLVED"
      ).length;

      const pieData = [
        { name: "Open", value: open },
        { name: "In Progress", value: progress },
        { name: "Resolved", value: resolved }
      ];

      const categoryMap = {};

      complaints.forEach(c => {
        categoryMap[c.category] =
          (categoryMap[c.category] || 0) + 1;
      });

      const categoryData = Object.keys(categoryMap).map(key => ({
        category: key,
        count: categoryMap[key]
      }));

      /* ===== Priority breakdown ===== */
      const priorityMap = { HIGH: 0, MEDIUM: 0, LOW: 0 };

      complaints.forEach(c => {
        const p = c.priority?.toUpperCase();
        if (p && priorityMap[p] !== undefined) priorityMap[p] += 1;
      });

      const priorityData = Object.keys(priorityMap).map(key => ({
        name: key.charAt(0) + key.slice(1).toLowerCase(),
        value: priorityMap[key]
      }));

      /* ===== Daily volume trend (last 14 days) ===== */
      const dayBuckets = {};
      const today = new Date();
      for (let i = 13; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        dayBuckets[key] = 0;
      }

      complaints.forEach(c => {
        const raw = getCreatedAt(c);
        if (!raw) return;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return;
        const key = d.toISOString().slice(0, 10);
        if (dayBuckets[key] !== undefined) dayBuckets[key] += 1;
      });

      const trendData = Object.keys(dayBuckets)
        .sort()
        .map(key => ({
          day: new Date(key).toLocaleDateString("en-US", { day: "2-digit", month: "short" }),
          count: dayBuckets[key]
        }));

      /* ===== Average resolution time (hours) ===== */
      const resolutionDurations = complaints
        .filter(c => c.status?.toUpperCase() === "RESOLVED")
        .map(c => {
          const start = getCreatedAt(c);
          const end = getResolvedAt(c);
          if (!start || !end) return null;
          const startMs = new Date(start).getTime();
          const endMs = new Date(end).getTime();
          if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return null;
          return (endMs - startMs) / (1000 * 60 * 60);
        })
        .filter(v => v !== null);

      const avgResolutionHours = resolutionDurations.length
        ? resolutionDurations.reduce((a, b) => a + b, 0) / resolutionDurations.length
        : null;

      setAnalytics({
        total,
        open,
        progress,
        resolved,
        pieData,
        categoryData,
        priorityData,
        trendData,
        avgResolutionHours,
        resolutionSampleSize: resolutionDurations.length
      });

    } catch (err) {
      console.error("Analytics fetch error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 15000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  if (loading) {
    return <div className="loading-container">Loading analytics&hellip;</div>;
  }

  return (
    <div className="modern-analytics">

      <div className="analytics-header">
        <p className="analytics-eyebrow">Ward Office &middot; Live Dashboard</p>
        <h1>Real-Time Analytics</h1>
      </div>

      {/* SUMMARY CARDS */}
      <div className="summary-grid">

        <div className="summary-card">
          <h4>Total</h4>
          <p>{analytics.total}</p>
        </div>

        <div className="summary-card open">
          <h4>Open</h4>
          <p>{analytics.open}</p>
        </div>

        <div className="summary-card progress">
          <h4>In Progress</h4>
          <p>{analytics.progress}</p>
        </div>

        <div className="summary-card resolved">
          <h4>Resolved</h4>
          <p>{analytics.resolved}</p>
        </div>

        <div className="summary-card resolution-time">
          <h4>Avg. Resolution</h4>
          <p>{formatHours(analytics.avgResolutionHours)}</p>
          {analytics.resolutionSampleSize > 0 && (
            <span className="summary-card-sub">from {analytics.resolutionSampleSize} resolved</span>
          )}
        </div>

      </div>

      {/* CHARTS */}
      <div className="charts-grid">

        <div className="chart-card">
          <h3>Status Distribution</h3>

          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={analytics.pieData}
                dataKey="value"
                nameKey="name"
                outerRadius={95}
                label
              >
                {analytics.pieData.map((entry) => (
                  <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>

        </div>

        <div className="chart-card">
          <h3>Priority Breakdown</h3>

          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={analytics.priorityData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={70} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {analytics.priorityData.map((entry) => (
                  <Cell key={entry.name} fill={PRIORITY_COLORS[entry.name.toUpperCase()]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

        </div>

        <div className="chart-card">
          <h3>Category Distribution</h3>

          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={analytics.categoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Complaints" fill={CATEGORY_BAR_COLOR} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

        </div>

        <div className="chart-card">
          <h3>Volume Trend (14 days)</h3>

          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={analytics.trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" interval="preserveStartEnd" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                name="New complaints"
                stroke={TREND_LINE_COLOR}
                strokeWidth={2.5}
                dot={{ r: 3, fill: TREND_LINE_COLOR }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>

        </div>

      </div>

    </div>
  );
};

export default Analytics;